import type { SavedRoute } from '../routeLibraryPersistence'
import type { RouteHistoryEntry } from './routeHistoryList'
import { canonicalId, makeRouteKey, type RouteKey } from './routeKey'

/**
 * Rewrites the ids a pre-v2 client stored in the route library (TASK-986):
 * route keys name stations by slug, and three v1 slugs no v2 station serves
 * (`kennington-northern-kng1` …) must become their station's slug; history
 * snapshots name lines, which were v1 ids (`line-nor`).
 *
 * Both maps return their input for an id that is already current. A retired
 * slug and its station's slug can name the same route twice: the earlier row
 * keeps its place and absorbs the later one. A route whose ends become one
 * station is no route and is dropped. Twin of iOS / Android
 * `RouteLibraryIdRewrite`.
 */
export interface RouteLibraryIdRewrite {
  readonly history: (entries: readonly RouteHistoryEntry[]) => RouteHistoryEntry[]
  readonly saved: (routes: readonly SavedRoute[]) => SavedRoute[]
}

export function makeRouteLibraryIdRewrite(
  slug: (slug: string) => string,
  lineId: (lineId: string) => string,
): RouteLibraryIdRewrite {
  const rewriteKey = (key: RouteKey): RouteKey | null =>
    makeRouteKey(slug(key.fromSlug), slug(key.toSlug))
  return {
    history(entries) {
      const merged = new Map<string, RouteHistoryEntry>()
      for (const entry of entries) {
        const key = rewriteKey(entry.key)
        if (key === null) continue
        const snapshot =
          entry.snapshot === null
            ? null
            : { ...entry.snapshot, lineIds: entry.snapshot.lineIds.map(lineId) }
        const id = canonicalId(key)
        const kept = merged.get(id)
        merged.set(
          id,
          kept === undefined
            ? { ...entry, key, snapshot }
            : {
                ...kept,
                lastUsedIso:
                  kept.lastUsedIso > entry.lastUsedIso ? kept.lastUsedIso : entry.lastUsedIso,
                useCount: kept.useCount + entry.useCount,
                snapshot: kept.snapshot ?? snapshot,
              },
        )
      }
      return [...merged.values()]
    },
    saved(routes) {
      const seen = new Set<string>()
      const kept: SavedRoute[] = []
      for (const route of routes) {
        const key = rewriteKey(route.key)
        if (key === null || seen.has(canonicalId(key))) continue
        seen.add(canonicalId(key))
        kept.push({ ...route, key })
      }
      return kept
    },
  }
}
